import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Plus, FileText, Share2, Trash2, Briefcase, Target, ListChecks,
  Loader2, Search, TrendingUp,
} from "lucide-react";
import { toast } from "sonner";
import Navbar from "../components/Navbar";
import ShareDialog from "../components/ShareDialog";
import QuoteBanner from "../components/QuoteBanner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import api from "../lib/apiClient";
import { exportPlanPDF } from "../lib/pdf";
import { swotProgress } from "../lib/pricing";
import { computeScore, SCORE_TONE } from "../lib/score";
import { useAuth } from "../context/AuthContext";
import PolicyConsentDialog from "../components/PolicyConsentDialog";

export default function Dashboard() {
  const navigate = useNavigate();
  const { user, acceptPolicy } = useAuth();
  const isPremium = user?.plan === "premium";
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ business_name: "", industry: "", description: "" });
  const [query, setQuery] = useState("");
  const [shareTarget, setShareTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [showPolicy, setShowPolicy] = useState(false);
  const [policyLoading, setPolicyLoading] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);

  // Runs `action` if the user already accepted the policy, otherwise opens the consent gate first.
  const guardPolicy = (action) => {
    if (user?.policy_accepted) { action(); return; }
    setPendingAction(() => action);
    setShowPolicy(true);
  };

  const onPolicyAccept = async () => {
    setPolicyLoading(true);
    try {
      await acceptPolicy();
      setShowPolicy(false);
      if (pendingAction) pendingAction();
      setPendingAction(null);
    } catch (e) {
      toast.error("Could not record your acceptance. Please try again.");
    } finally {
      setPolicyLoading(false);
    }
  };

  const load = async () => {
    try {
      const res = await api.get("/plans");
      setPlans(res.data);
    } catch (e) {
      toast.error("Failed to load plans");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const doCreatePlan = async () => {
    setCreating(true);
    try {
      const res = await api.post("/plans", form);
      navigate(`/plan/${res.data.plan_id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create plan");
      setCreating(false);
    }
  };

  const createPlan = () => {
    if (!form.business_name.trim()) { toast.error("Business name is required"); return; }
    guardPolicy(doCreatePlan);
  };

  const exportPdf = (p) => guardPolicy(() => exportPlanPDF(p, { premium: isPremium }));

  const remove = async () => {
    const t = deleteTarget;
    setDeleteTarget(null);
    try {
      await api.delete(`/plans/${t.plan_id}`);
      setPlans((p) => p.filter((x) => x.plan_id !== t.plan_id));
      toast.success("Plan deleted");
    } catch (e) { toast.error("Delete failed"); }
  };

  const filtered = plans.filter((p) =>
    p.business_name.toLowerCase().includes(query.toLowerCase()) ||
    (p.industry || "").toLowerCase().includes(query.toLowerCase())
  );

  const stats = {
    total: plans.length,
    completed: plans.filter((p) => swotProgress(p.swot) === 100).length,
    pending: plans.reduce((n, p) => n + ["phase1", "phase2", "phase3"].reduce((m, ph) => m + (p.action_plan?.[ph]?.filter((t) => !t.done).length || 0), 0), 0),
  };

  return (
    <div className="min-h-screen grid-bg text-slate-100">
      <Navbar>
        <Button data-testid="new-plan-nav-button" onClick={() => setShowCreate(true)} className="rounded-full cta-btn font-semibold">
          <Plus className="h-4 w-4 mr-1" /> New Plan
        </Button>
      </Navbar>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
          <div>
            <h1 className="font-display text-3xl sm:text-4xl font-extrabold">Your evaluations</h1>
            <p className="text-slate-400 mt-1">Build, refine and share your business plans.</p>
          </div>
          <div className="relative w-full sm:w-72">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
            <Input
              data-testid="search-plans-input"
              placeholder="Search plans…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="pl-9 bg-slate-900/70 border-slate-700"
            />
          </div>
        </div>

        <div className="grid grid-cols-3 gap-3 sm:gap-5 mt-6">
          {[
            { label: "Total Plans", value: stats.total, icon: Briefcase, color: "text-indigo-400" },
            { label: "Completed SWOTs", value: stats.completed, icon: Target, color: "text-emerald-400" },
            { label: "Open Action Items", value: stats.pending, icon: ListChecks, color: "text-rose-400" },
          ].map((s) => (
            <div key={s.label} className="rounded-2xl border border-slate-800 bg-slate-900/60 p-4 sm:p-5">
              <s.icon className={`h-5 w-5 ${s.color}`} />
              <div className="font-display text-2xl sm:text-3xl font-bold mt-2">{s.value}</div>
              <div className="text-xs sm:text-sm text-slate-400">{s.label}</div>
            </div>
          ))}
        </div>

        <div className="mt-6">
          <QuoteBanner />
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-indigo-400" /></div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20 border border-dashed border-slate-800 rounded-3xl mt-8">
            <TrendingUp className="h-10 w-10 text-slate-600 mx-auto" />
            <p className="mt-4 text-slate-400">{plans.length === 0 ? "No plans yet. Create your first business evaluation." : "No plans match your search."}</p>
            {plans.length === 0 && (
              <Button data-testid="empty-create-button" onClick={() => setShowCreate(true)} className="mt-5 rounded-full cta-btn">
                <Plus className="h-4 w-4 mr-1" /> Create plan
              </Button>
            )}
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-8">
            {filtered.map((p, i) => {
              const sc = computeScore(p);
              const tone = SCORE_TONE[sc.tone];
              return (
                <motion.div
                  key={p.plan_id}
                  data-testid={`plan-card-${p.plan_id}`}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: i * 0.05 }}
                  className="group rounded-2xl border border-slate-800 bg-slate-900/60 p-6 hover:border-indigo-500/50 transition-all duration-300 flex flex-col"
                >
                  <div onClick={() => navigate(`/plan/${p.plan_id}`)} className="cursor-pointer flex-1">
                    <div className="flex items-start justify-between gap-2">
                      <h3 className="font-display text-lg font-bold group-hover:text-indigo-300 transition">{p.business_name}</h3>
                      {p.industry && <Badge className="bg-slate-800 text-slate-300 shrink-0">{p.industry}</Badge>}
                    </div>
                    <p className="text-sm text-slate-400 mt-2 line-clamp-2 min-h-[40px]">{p.description || "No description yet."}</p>
                    <div className="mt-4">
                      <div className="flex justify-between text-xs text-slate-400 mb-1.5">
                        <span>Readiness · {sc.score}/100</span>
                        <Badge className={`${tone.badge} text-[10px]`}>{sc.grade}</Badge>
                      </div>
                      <Progress value={sc.score} className="h-1.5 bg-slate-800" />
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 mt-5 pt-4 border-t border-slate-800">
                    <Button data-testid={`export-pdf-${p.plan_id}`} variant="ghost" size="sm" onClick={() => exportPdf(p)} className="text-slate-300 hover:text-white hover:bg-slate-800">
                      <FileText className="h-4 w-4 mr-1" /> PDF
                    </Button>
                    <Button data-testid={`share-${p.plan_id}`} variant="ghost" size="sm" onClick={() => setShareTarget(p)} className="text-slate-300 hover:text-white hover:bg-slate-800">
                      <Share2 className="h-4 w-4 mr-1" /> Share
                    </Button>
                    <Button data-testid={`delete-${p.plan_id}`} variant="ghost" size="sm" onClick={() => setDeleteTarget(p)} className="ml-auto text-rose-400 hover:text-rose-300 hover:bg-rose-500/10">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>

      {/* Create dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent data-testid="create-plan-dialog" className="bg-slate-900 border-slate-700 text-slate-100">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">New business evaluation</DialogTitle>
            <DialogDescription className="text-slate-400">We'll auto-draft your SWOT, competitors, pricing and 90-day plan from this — the more detail, the better.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm text-slate-300">Business / idea name *</label>
              <Input data-testid="create-name-input" value={form.business_name} onChange={(e) => setForm({ ...form, business_name: e.target.value })} placeholder="e.g. BrewLab Coffee Truck" className="mt-1.5 bg-slate-800 border-slate-700" />
            </div>
            <div>
              <label className="text-sm text-slate-300">Industry / niche</label>
              <Input data-testid="create-industry-input" value={form.industry} onChange={(e) => setForm({ ...form, industry: e.target.value })} placeholder="e.g. Mobile food & beverage" className="mt-1.5 bg-slate-800 border-slate-700" />
            </div>
            <div>
              <label className="text-sm text-slate-300">Short description</label>
              <Textarea data-testid="create-desc-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="What does the business do, and who is it for?" className="mt-1.5 bg-slate-800 border-slate-700" rows={3} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowCreate(false)} className="hover:bg-slate-800">Cancel</Button>
            <Button data-testid="create-submit-button" onClick={createPlan} disabled={creating} className="cta-btn">
              {creating ? <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Drafting plan…</> : "Create & auto-draft"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {shareTarget && (
        <ShareDialog
          open={!!shareTarget}
          onOpenChange={(v) => !v && setShareTarget(null)}
          plan={shareTarget}
          onShared={(tok) => setPlans((ps) => ps.map((x) => x.plan_id === shareTarget.plan_id ? { ...x, share_token: tok } : x))}
        />
      )}

      <AlertDialog open={!!deleteTarget} onOpenChange={(v) => !v && setDeleteTarget(null)}>
        <AlertDialogContent className="bg-slate-900 border-slate-700 text-slate-100">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this plan?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">"{deleteTarget?.business_name}" will be permanently removed. This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-slate-800 border-slate-700 hover:bg-slate-700 text-slate-100">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-button" onClick={remove} className="bg-rose-600 hover:bg-rose-500">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <PolicyConsentDialog
        open={showPolicy}
        onOpenChange={(v) => { setShowPolicy(v); if (!v) setPendingAction(null); }}
        onAccept={onPolicyAccept}
        loading={policyLoading}
      />
    </div>
  );
}
